import React, { type FC } from 'react';
import { useHistory } from 'react-router';
import { compiler } from 'markdown-to-jsx';

import { API, useFetch } from 'app/api';
import { Button, ButtonIntent, ButtonType } from 'app/design-system/components/Button';
import { Modal } from 'app/design-system/components/Modal';
import { Scrollbar } from 'app/design-system/components/Scrollbar';
import { Spinner } from 'app/design-system/components/Spinner';
import { useTranslation } from 'app/lang';
import { useComponentWillReceiveProps } from 'app/packages/lib/hooks';
import LinkWrapper from 'app/widgets/LinkWrapper';

import PromotionNoData from '../PromotionNoData/PromotionNoData';
import { PROMOTION_MODAL_PARAM_KEY, PROMOTION_MODAL_WIDTH } from './constants';

import dictionary from './PromotionModal.dictionary.json';

import styles from './PromotionModal.module.scss';

const PromotionModal: FC = () => {
  const { t } = useTranslation();
  const { location, replace } = useHistory();

  const { data, loading, makeRequest } = useFetch(
    API.strapiServices.documents.getPromotion,
    { ssrUniqKey: 'promo' },
  );

  const urlParams = new URLSearchParams(location.search);

  const promotionId: string = urlParams.get(PROMOTION_MODAL_PARAM_KEY) || '';
  const isOpen = urlParams.has(PROMOTION_MODAL_PARAM_KEY);

  const onClose = () => replace({ search: '' });

  useComponentWillReceiveProps(() => {
    if (promotionId){
      makeRequest(promotionId);
    }
  }, [promotionId]);

  const getModalContent = () => {
    if (loading) {
      return (
        <Spinner />
      );
    }
    if (!data?.content) {
      return (
        <div className={styles.noData}>
          <PromotionNoData title={t(dictionary.noData)} />
        </div>
      );
    }
    return compiler(data.content, {
      wrapper: null,
      overrides: {
        a: { component: LinkWrapper },
      },
    });
  };

  return (
    <Modal isOpen={isOpen}
           onClose={onClose}
           maxWidth={PROMOTION_MODAL_WIDTH}>
      <Modal.Header>
        {data?.name}
      </Modal.Header>
      <Modal.Body className={styles.content}>
        <Scrollbar className={styles.modalBodyScroll}>
          {getModalContent()}
        </Scrollbar>
      </Modal.Body>
      <Modal.Footer>
        <Button type={ButtonType.BUTTON}
                intent={ButtonIntent.CALL_TO_ACTION}
                onClick={onClose}>
          {t(dictionary.button)}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PromotionModal;
